import jsCookie from 'js-cookie'
import {createContext, FC, PropsWithChildren, useContext, useEffect, useState} from 'react'

import {getCurrentSearchParams} from '@/utils/browser/location'
import {currencyCookieName, currencyParamName, localeCookieName} from '@/utils/constants/http'
import {currencies, Currency} from '@/utils/currency'
import {getLanguage} from '@/utils/i18n/language'
import {saveLanguageChoice} from '@/utils/preferences/choices'

import {usePreferences} from './Preferences'
import {useUserData} from './UserData'

/*
 * Applies a signed-in visitor's profile to this device. The profile is the source of truth for
 * their language and currency, so those cookies mirror it - restored when they lapse, and
 * following a change made on another device - and its timezone is used. See
 * docs/visitor-preferences.md.
 */

type ProfilePreferencesContextType = {
  // True until the currency is final: preferences resolved, the session known and, when signed in,
  // the profile's currency applied - so a placeholder shows instead of a currency about to change
  isCurrencyPending: boolean
}

const ProfilePreferencesContext = createContext<ProfilePreferencesContextType>({
  isCurrencyPending: false,
})

export const useProfilePreferences = (): ProfilePreferencesContextType =>
  useContext(ProfilePreferencesContext)

export const ProfilePreferencesProvider: FC<PropsWithChildren<unknown>> = ({children}) => {
  const {user, isAuthPending} = useUserData()
  const {setTimezone, setCurrency, isPreferencesPending} = usePreferences()
  const profile = user?.profile

  useEffect(() => {
    if (profile?.timezone) {
      setTimezone(profile.timezone)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.timezone])

  useEffect(() => {
    const profileLanguage = profile?.language
    if (profileLanguage && jsCookie.get(localeCookieName) !== getLanguage(profileLanguage)) {
      saveLanguageChoice(getLanguage(profileLanguage))
    }
  }, [profile?.language])

  // The currency waits for the page's own preferences, which would otherwise resolve over it, and
  // leaves a campaign link's ?currency= to the page it opened. The user is marked done in the same
  // update that applies the currency, so nothing shows the one before it.
  const [currencySyncedUserId, setCurrencySyncedUserId] = useState<string | null>(null)
  useEffect(() => {
    if (isPreferencesPending || isAuthPending || !user) {
      return
    }
    const profileCurrency = profile?.currency
    if (
      profileCurrency &&
      profileCurrency in currencies &&
      !getCurrentSearchParams().has(currencyParamName) &&
      jsCookie.get(currencyCookieName) !== profileCurrency
    ) {
      setCurrency(profileCurrency as Currency)
    }
    setCurrencySyncedUserId(user.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, profile?.currency, isAuthPending, isPreferencesPending])

  const isCurrencyPending =
    isPreferencesPending || isAuthPending || (user !== null && currencySyncedUserId !== user.id)

  return (
    <ProfilePreferencesContext.Provider value={{isCurrencyPending}}>
      {children}
    </ProfilePreferencesContext.Provider>
  )
}
